/**
 * Email Typo Detector Utility
 * Detects common typos in email addresses
 */

interface TypoPattern {
  pattern: RegExp;
  suggestion: string;
  description: string;
}

const commonTypos: TypoPattern[] = [
  // Gmail variants
  { pattern: /@gmai\.com$/i, suggestion: '@gmail.com', description: 'Did you mean "gmail.com"?' },
  { pattern: /@gamil\.com$/i, suggestion: '@gmail.com', description: 'Did you mean "gmail.com"?' },
  { pattern: /@gmial\.com$/i, suggestion: '@gmail.com', description: 'Did you mean "gmail.com"?' },
  { pattern: /@gmaill\.com$/i, suggestion: '@gmail.com', description: 'Did you mean "gmail.com"?' },
  { pattern: /@gmqil\.com$/i, suggestion: '@gmail.com', description: 'Did you mean "gmail.com"?' },
  { pattern: /@gmali\.com$/i, suggestion: '@gmail.com', description: 'Did you mean "gmail.com"?' },
  
  // Common TLD typos
  { pattern: /\.con$/i, suggestion: '.com', description: 'Did you mean ".com"?' },
  { pattern: /\.cmo$/i, suggestion: '.com', description: 'Did you mean ".com"?' },
  { pattern: /\.coom$/i, suggestion: '.com', description: 'Did you mean ".com"?' },
  { pattern: /\.co$/i, suggestion: '.com', description: 'Did you mean ".com"?' },
  { pattern: /\.comm$/i, suggestion: '.com', description: 'Did you mean ".com"?' },
  
  // Yahoo variants
  { pattern: /@yahooo\.com$/i, suggestion: '@yahoo.com', description: 'Did you mean "yahoo.com"?' },
  { pattern: /@yaho\.com$/i, suggestion: '@yahoo.com', description: 'Did you mean "yahoo.com"?' },
  { pattern: /@yahol\.com$/i, suggestion: '@yahoo.com', description: 'Did you mean "yahoo.com"?' },
  
  // Outlook/Hotmail variants
  { pattern: /@hotmial\.com$/i, suggestion: '@hotmail.com', description: 'Did you mean "hotmail.com"?' },
  { pattern: /@hotmali\.com$/i, suggestion: '@hotmail.com', description: 'Did you mean "hotmail.com"?' },
  { pattern: /@outlok\.com$/i, suggestion: '@outlook.com', description: 'Did you mean "outlook.com"?' },
  { pattern: /@outloook\.com$/i, suggestion: '@outlook.com', description: 'Did you mean "outlook.com"?' },
];

interface TypoDetectionResult {
  hasTypo: boolean;
  originalEmail: string;
  suggestedEmail?: string;
  description?: string;
}

/**
 * Detects potential typos in an email address
 * @param email - The email address to check
 * @returns TypoDetectionResult object with typo detection details
 */
export const detectEmailTypo = (email: string): TypoDetectionResult => {
  if (!email || typeof email !== 'string') {
    return { hasTypo: false, originalEmail: email };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // Check against all common typo patterns
  for (const typo of commonTypos) {
    if (typo.pattern.test(trimmedEmail)) {
      const suggestedEmail = trimmedEmail.replace(typo.pattern, typo.suggestion);
      return {
        hasTypo: true,
        originalEmail: email,
        suggestedEmail,
        description: typo.description,
      };
    }
  }

  return { hasTypo: false, originalEmail: email };
};

/**
 * Get a user-friendly warning message for email typo
 * @param result - The typo detection result
 * @returns A formatted warning message
 */
export const getTypoWarningMessage = (result: TypoDetectionResult): string => {
  if (!result.hasTypo) {
    return '';
  }

  return `We noticed a possible typo in your email address:\n\n` +
    `You entered: ${result.originalEmail}\n` +
    `${result.description}\n` +
    `Suggested: ${result.suggestedEmail}\n\n` +
    `Please make sure your email is correct to receive your tickets.`;
};
